import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { seriesApi } from '../../services/api';

interface Series {
  id: number;
  name: string;
}

interface SeriesAutocompleteProps {
  value: Series | null;
  onChange: (value: Series | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const SeriesAutocomplete: React.FC<SeriesAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Series[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch series when search term changes
  useEffect(() => {
    let active = true;

    const fetchSeries = async () => {
      setLoading(true);
      try {
        let params: any = { page_size: 10 };
        if (searchTerm) {
          params.search = searchTerm;
        } else {
          params.timeline = 'ONGOING';
        }
        const response = await seriesApi.getAll(params);
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching series:', error);
        if (active) setOptions([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSeries();

    return () => {
      active = false;
    };
  }, [searchTerm]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      size='small'
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Series"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SeriesAutocomplete;